import { Link, useLocation } from 'react-router-dom';
import { BookOpen, ExternalLink } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { menuStructure, DOCUMENTATION_URL, type MenuItem, type MenuGroup } from './nav-menu-structure';
import { useSidebarNavPermissions } from './useSidebarNavPermissions';

interface SidebarMenuProps {
  onItemClick?: () => void;
}

export function SidebarMenu({ onItemClick }: SidebarMenuProps) {
  const location = useLocation();
  const { user } = useAuth();
  const { visibleItems, pendingApprovals } = useSidebarNavPermissions(user);

  const isActive = (path: string) => {
    if (path === '/dashboard' && location.pathname === '/') return true;
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const renderMenuItem = (item: MenuItem) => {
    if (!visibleItems.has(item.path)) {
      return null;
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={onItemClick}
        className={cn(
          'flex items-center px-3 py-2 rounded-lg transition-colors text-sm',
          isActive(item.path)
            ? 'bg-blue-600 text-white font-medium dark:bg-blue-500'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
        )}
      >
        <span className="mr-3">{item.icon}</span>
        <span className="flex-1">{item.name}</span>
        {item.path === '/approvals' && pendingApprovals > 0 && (
          <span
            className={cn(
              'ml-2 inline-flex items-center justify-center',
              'min-w-[18px] h-[18px] px-1 text-xs font-bold',
              isActive(item.path) ? 'bg-white/20 text-white' : 'bg-red-500 text-white',
              pendingApprovals > 99 && 'text-[10px]'
            )}
            style={{ borderRadius: 9999 }}
            aria-label={`${pendingApprovals} pending approvals`}
          >
            {pendingApprovals > 99 ? '99+' : pendingApprovals}
          </span>
        )}
      </Link>
    );
  };

  const renderMenuGroup = (group: MenuGroup, index: number) => {
    const visibleGroupItems = group.items.filter((item) => visibleItems.has(item.path));

    if (visibleGroupItems.length === 0) {
      return null;
    }

    return (
      <div key={`group-${index}`} className="space-y-1">
        <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
          {group.title}
        </div>
        {visibleGroupItems.map(renderMenuItem)}
      </div>
    );
  };

  return (
    <nav className="space-y-4">
      {menuStructure.map((item, index) => {
        if ('items' in item) {
          return renderMenuGroup(item, index);
        }
        return <div key={item.path}>{renderMenuItem(item)}</div>;
      })}
      <a
        href={DOCUMENTATION_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onItemClick}
        className="flex items-center px-3 py-2 rounded-lg transition-colors text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        <BookOpen className="w-5 h-5 mr-3 shrink-0" />
        <span className="flex-1">Documentation</span>
        <ExternalLink className="w-3 h-3 ml-2 shrink-0 opacity-50" aria-hidden />
      </a>
    </nav>
  );
}
